import { Link } from "react-router-dom";
import { Facebook, Twitter, Instagram, Youtube, Mail, Phone, MapPin } from "lucide-react";

const ABFA_SOCIALS = [
  { key: "facebook", label: "Facebook", href: "https://www.facebook.com/antiguafootbal", Icon: Facebook },
  { key: "instagram", label: "Instagram", href: "https://www.instagram.com/antiguaandbarbudafa/", Icon: Instagram },
  { key: "twitter", label: "X", href: "https://x.com/AntiguaFA", Icon: Twitter },
  { key: "youtube", label: "YouTube", href: "https://www.youtube.com/@antiguaandbarbudafootball", Icon: Youtube },
];

const LOGO_URL = "https://customer-assets.emergentagent.com/job_soccer-pro-21/artifacts/6hs5jyaw_image-removebg-preview.png";

// ABFA sits in the centre of its affiliated bodies
const AFFILIATES = [
  { key: "apl", name: "Antigua Premier League", src: "/affiliates/antigua-premier-league.webp", h: "h-16 md:h-20" },
  { key: "fifa", name: "FIFA", src: "/affiliates/fifa.png", h: "h-8 md:h-11", href: "https://www.fifa.com" },
  { key: "abfa", name: "Antigua & Barbuda Football Association", src: "/affiliates/abfa.png", h: "h-20 md:h-28" },
  { key: "concacaf", name: "Concacaf", src: "/affiliates/concacaf.png", h: "h-14 md:h-20", href: "https://www.concacaf.com" },
  { key: "cfu", name: "Caribbean Football Union", src: "/affiliates/cfu.png", h: "h-12 md:h-16", href: "https://www.cfufootball.org" },
];

const Affiliations = () => (
  <section className="bg-white border-t border-gray-200" data-testid="affiliations">
    <div className="max-w-6xl mx-auto px-4 md:px-8 py-8 md:py-10">
      <p className="text-center text-[10px] md:text-xs font-bold uppercase tracking-[0.3em] text-gray-400 mb-6">Proud Member Of</p>
      <ul className="grid grid-cols-5 items-center justify-items-center gap-3 md:gap-10">
        {AFFILIATES.map((a) => (
          <li key={a.key} className={a.key === "abfa" ? "md:px-4" : ""} data-testid={`affiliate-${a.key}`}>
            {a.href ? (
              <a href={a.href} target="_blank" rel="noopener noreferrer" aria-label={`${a.name} website`} data-testid={`affiliate-link-${a.key}`}>
                <img src={a.src} alt={a.name} title={a.name} loading="lazy" className={`${a.h} w-auto max-w-full object-contain transition-transform duration-300 hover:scale-105`} />
              </a>
            ) : (
              <img src={a.src} alt={a.name} title={a.name} loading="lazy" className={`${a.h} w-auto max-w-full object-contain transition-transform duration-300 hover:scale-105`} />
            )}
          </li>
        ))}
      </ul>
    </div>
  </section>
);

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <>
    <Affiliations />
    <footer className="bg-[#09090b] text-white" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Logo & About */}
          <div>
            <Link to="/" className="flex items-center gap-3 mb-6">
              <img src={LOGO_URL} alt="ABFA Football Centre" className="h-16 w-auto" />
              <div>
                <p className="font-heading font-bold text-lg uppercase leading-none text-white">ABFA</p>
                <p className="text-[10px] text-[#FFC72C] uppercase tracking-widest font-bold">Football Centre</p>
              </div>
            </Link>
            <p className="text-gray-400 text-sm leading-relaxed mb-6">
              The Antigua and Barbuda Football Association (ABFA) is the governing body of football in Antigua and Barbuda. Founded in 1928, we are dedicated to developing and promoting football throughout the nation.
            </p>
            <div className="flex gap-4">
              {ABFA_SOCIALS.map(({ key, href, label, Icon }) => (
                <a key={key} href={href} target="_blank" rel="noopener noreferrer" aria-label={`ABFA on ${label}`} title={label} className="p-2 bg-gray-800 hover:bg-[#FFC72C] hover:text-black transition-colors" data-testid={`social-${key}`}>
                  <Icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-heading font-bold text-lg uppercase tracking-wider mb-6">Quick Links</h3>
            <ul className="space-y-3">
              {[
                { href: "/competitions", label: "Competitions" },
                { href: "/teams", label: "Teams" },
                { href: "/fixtures", label: "Fixtures & Results" },
                { href: "/news", label: "News" },
                { href: "/media", label: "Media Gallery" },
                { href: "/contact", label: "Contact Us" },
              ].map((link) => (
                <li key={link.href}>
                  <Link 
                    to={link.href}
                    className="text-gray-400 hover:text-[#FFC72C] transition-colors text-sm"
                    data-testid={`footer-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Competitions */}
          <div>
            <h3 className="font-heading font-bold text-lg uppercase tracking-wider mb-6">Competitions</h3>
            <ul className="space-y-3">
              {[
                "Premier Division",
                "First Division",
                "Women's League",
                "Youth League",
                "FA Cup",
              ].map((comp) => (
                <li key={comp}>
                  <Link 
                    to="/competitions"
                    className="text-gray-400 hover:text-[#FFC72C] transition-colors text-sm"
                  >
                    {comp}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="font-heading font-bold text-lg uppercase tracking-wider mb-6">Contact Us</h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-[#FFC72C] flex-shrink-0 mt-0.5" />
                <span className="text-gray-400 text-sm">
                  ABFA Technical Centre<br />
                  Paynters Road<br />
                  St. John's, Antigua
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-[#FFC72C]" />
                <a href="tel:+12685626014" className="text-gray-400 hover:text-[#FFC72C] transition-colors text-sm" data-testid="footer-phone-link">
                  +1 (268) 562-6014
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-[#FFC72C]" />
                <a href="mailto:gensec@footballanu.org" className="text-gray-400 hover:text-[#FFC72C] transition-colors text-sm">
                  gensec@footballanu.org
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 mt-12 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-500 text-sm">
              © {currentYear} Antigua and Barbuda Football Association. All rights reserved.
            </p>
            <div className="flex flex-wrap items-center gap-6">
              <a href="#" className="text-gray-500 hover:text-[#FFC72C] text-sm transition-colors">Privacy Policy</a>
              <a href="#" className="text-gray-500 hover:text-[#FFC72C] text-sm transition-colors">Terms of Service</a>
              <a
                href="https://datizit.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-500 hover:text-[#FFC72C] text-sm transition-colors inline-flex items-center gap-1"
                data-testid="footer-powered-by"
              >
                Powered by <span className="font-semibold text-gray-300 hover:text-[#FFC72C]">Digital Ambassador</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
    </>
  );
};

export default Footer;
